import { useMemo } from 'react';
import { BankIntegrationProvider, DbsPayloadScheme } from '../../../../api/types';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export const createBankIntegrationValidator = (provider: BankIntegrationProvider, isUpdate = false) => {
    switch (provider) {
        case BankIntegrationProvider.Email: {
            return validators.compose(validators.requiredString('email'), validators.validEmail('email'));
        }

        case BankIntegrationProvider.Hlf:
        case BankIntegrationProvider.Hlfv2: {
            return validators.compose(
                validators.requiredString('allowedIps'),
                validators.requiredString('baseUrl'),
                validators.requiredString('submissionPartial'),
                validators.requiredString('editionPartial'),
                validators.requiredString('cancellationPartial'),
                validators.requiredString('reassignPartial'),
                validators.requiredString('privateKey'),
                validators.requiredString('publicCert'),
                validators.only(
                    () => provider === BankIntegrationProvider.Hlfv2,
                    validators.requiredString('dealerCode')
                )
            );
        }

        case BankIntegrationProvider.Uob: {
            return validators.compose(
                validators.requiredString('allowedIps'),
                validators.requiredString('host'),
                validators.requiredString('clientId'),
                validators.requiredString('applicationId'),
                validators.requiredString('apiKey'),
                validators.requiredString('sslPrivateKey'),
                validators.requiredString('sslPublicCert'),
                validators.requiredString('encryptionPrivateKey'),
                validators.requiredString('encryptionPublicCert'),
                validators.requiredString('signaturePrivateKey')
            );
        }

        case BankIntegrationProvider.Dbs: {
            return validators.compose(
                validators.requiredString('allowedIps'),
                validators.requiredString('host'),
                validators.requiredString('orgId'),
                validators.requiredString('apiKey'),
                validators.requiredString('privateKey'),
                validators.requiredString('publicCert'),
                validators.forEach(
                    'mappings',
                    validators.compose(
                        validators.requiredNumber('appliedInterestRate'),
                        validators.requiredString('schemeCode'),
                        validators.requiredString('packageCode')
                    )
                ),
                validators.requiredStringEnum('payloadScheme', DbsPayloadScheme)
            );
        }

        case BankIntegrationProvider.BmwFs: {
            return validators.compose(
                validators.requiredString('host'),
                validators.requiredString('accessKey'),
                validators.only(() => !isUpdate, validators.requiredString('secretKey'))
            );
        }

        case BankIntegrationProvider.Maybank: {
            return validators.compose(
                validators.requiredString('allowedIps'),
                validators.requiredString('dealerId'),
                validators.requiredString('host'),
                validators.requiredString('clientId'),
                validators.requiredString('apiKey'),
                validators.requiredString('environment'),
                validators.requiredString('clientSecret'),
                validators.requiredString('encryptionPublicCert'),
                validators.requiredString('signaturePublicCert'),
                validators.only(
                    () => !isUpdate,
                    validators.compose(
                        validators.requiredString('encryptionPrivateKey'),
                        validators.requiredString('signaturePrivateKey')
                    )
                )
            );
        }

        case BankIntegrationProvider.Enbd: {
            return validators.compose(
                validators.requiredString('companyName'),
                validators.requiredString('financeManager.name'),
                validators.validEmail('financeManager.email')
            );
        }

        default:
            throw new Error(`invalid bank integration provider: ${provider}`);
    }
};

const useBankIntegrationFormValidation = (provider: BankIntegrationProvider) => {
    const validator = useMemo(() => createBankIntegrationValidator(provider, true), [provider]);

    return useValidator(validator);
};

export default useBankIntegrationFormValidation;
